'use client';

import { PlayerInfoPopover } from './PlayerInfoPopover';
import { formatPlayerName } from '@/utils/team-utils';
import type { Player } from '@/types/tournament.model';
import type { EloCategory } from './models/elo.model';
import { formatElo, getCategoryByElo } from './utils';

export type PlayersNamesRankingMeta = {
  sportUid: string;
  eloCategories: EloCategory[];
};

function playerRankingSummary(
  player: Player,
  sportUid: string,
  eloCategories: EloCategory[],
): { line: string; title: string } {
  const ranking = player.rankings?.find((r) => r.sport?.uid === sportUid);
  if (!ranking) {
    return { line: '—', title: 'Sin ranking en este deporte' };
  }
  const elo = formatElo(ranking.elo);
  const eloPrefix = `Elo ${elo}`;
  const categoryName =
    eloCategories.length > 0
      ? getCategoryByElo(eloCategories, ranking.elo)
      : null;
  if (categoryName) {
    const line = `${eloPrefix} · ${categoryName}`;
    return { line, title: line };
  }
  return { line: eloPrefix, title: eloPrefix };
}

interface PlayersNamesListProps {
  players: Player[] | undefined;
  fallbackName?: string;
  className?: string;
  stackOnMobile?: boolean;
  /** Second row under names: compact Elo + category per player (group standings, etc.). */
  rankingMeta?: PlayersNamesRankingMeta;
}

export function PlayersNamesList({
  players,
  fallbackName,
  className = 'text-sm font-medium text-gray-900',
  stackOnMobile = false,
  rankingMeta,
}: PlayersNamesListProps) {
  if (!players || players.length === 0) {
    return fallbackName ? (
      <span className={className}>{fallbackName}</span>
    ) : null;
  }

  const eloPopoverProps =
    rankingMeta != null
      ? {
          sportUid: rankingMeta.sportUid,
          eloCategories: rankingMeta.eloCategories,
        }
      : {};

  const namesRow = (
    <div
      className={
        stackOnMobile
          ? 'flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-1'
          : 'flex min-w-0 items-center gap-1'
      }
    >
      {players.map((player, idx) => (
        <span
          key={player.id || idx}
          className={
            stackOnMobile ? 'flex min-w-0 items-center' : 'flex items-center'
          }
        >
          {idx > 0 && (
            <span
              className={
                stackOnMobile
                  ? 'mr-1 hidden text-sm text-gray-400 sm:inline'
                  : 'mr-1 text-sm text-gray-400'
              }
            >
              /
            </span>
          )}
          <PlayerInfoPopover player={player} {...eloPopoverProps}>
            <span
              className={`cursor-pointer hover:text-blue-600 hover:underline ${
                stackOnMobile ? `min-w-0 truncate ${className}` : className
              }`}
            >
              {formatPlayerName(player.name)}
            </span>
          </PlayerInfoPopover>
        </span>
      ))}
    </div>
  );

  if (!rankingMeta) {
    return namesRow;
  }

  const { sportUid, eloCategories } = rankingMeta;

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      {namesRow}
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-1 text-[11px] leading-snug text-gray-500">
        {players.map((player, idx) => {
          const { line, title } = playerRankingSummary(
            player,
            sportUid,
            eloCategories,
          );
          return (
            <span
              key={`meta-${player.id || idx}`}
              className="flex min-w-0 max-w-[11rem] items-center"
            >
              {idx > 0 && (
                <span className="mx-1 shrink-0 text-gray-300" aria-hidden>
                  /
                </span>
              )}
              <span className="truncate" title={title}>
                {line}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

